<template>
    <div class="report-content">
        <div class="graphics">
            <Hide v-model="chartVisibility">
                <h4>{{ $t('reports.callStatistics') }}</h4>
            </Hide>

            <div v-show="chartVisibility">
                <div v-show="callStatisticsLoaded" class="pie-chart-wrapper">
                    <div class="full-event ct-chart ct-major-tenth"></div>
                </div>
                <LoadingBlock v-if="chartLoader" />
                <h4 v-if="!chartLoader && (!callStatistics || !emptyCallStatistics)" class="data-empty">
                    {{ $t('common.noData') }}
                </h4>
            </div>
        </div>

        <div class="detailing">
            <Hide v-model="tableVisibility">
                <h4 ref="details">{{ $t('reports.detailed') }}</h4>
            </Hide>
        </div>
        <div v-show="tableVisibility">
            <Pagination
                v-if="fullEventLength > 5"
                v-model="pagination.current_page"
                :options="pagination"
                :callback="handlePaginationCallback"
                :to-cache-pages="toCachePages"
            />
            <table
                v-if="!tableLoader && reportData.length > 0"
                id="forAT_table-FullEventReport"
                class="common-table"
            >
                <tr>
                    <th>{{ $t('reports.fullEvent.column.area') }}</th>
                    <th>{{ $t('reports.fullEvent.column.location') }}</th>
                    <th>{{ $t('reports.fullEvent.column.dateTime') }}</th>
                    <th>{{ $t('reports.fullEvent.column.event') }}</th>
                    <th>{{ $t('reports.fullEvent.column.targetTime') }}</th>
                    <th>{{ $t('reports.fullEvent.column.actualTime') }}</th>
                </tr>
                <tbody>
                    <template v-for="(callTrace, index) in reportData">
                        <tr
                            v-for="(detail, index2) in callTrace.details"
                            :key="index + '-' + index2"
                            :class="{ marker: hasMarker(callTrace.id) }"
                        >
                            <td class="area">{{ callTrace.nameAA }}</td>
                            <td class="location">{{ callTrace.getLocation() }}</td>
                            <td>
                                <div class="date">{{ detail.getStartSDate() }}</div>
                                <div class="date">{{ detail.getStartSTime() }}</div>
                            </td>
                            <td>
                                <img
                                    v-if="showColoredIcon(detail.icons[0])"
                                    :src="'static/img/' + iconClass(detail.icons[0]) + '.svg'"
                                    alt=""
                                />
                                <i
                                    v-else
                                    :class="[
                                        'icon-' + iconClass(detail.icons[0]),
                                        { canceled: detail.icons[1] },
                                    ]"
                                ></i>
                                {{ $t(detail.getTranslationKey(), detail.getTranslationParams()) }}
                            </td>
                            <td>{{ detail.getTargetTime() }}</td>
                            <td>
                                {{
                                    detail.isUnfinished() && detail.isFirst()
                                        ? $t(detail.getUnfinishedTranslationKey())
                                        : detail.getActualTime()
                                }}
                            </td>
                        </tr>
                    </template>
                </tbody>
            </table>
            <LoadingBlock v-if="tableLoader" />
            <Pagination
                v-if="fullEventLength > 5"
                v-model="pagination.current_page"
                :options="pagination"
                :callback="handlePaginationCallback"
                :to-cache-pages="toCachePages"
            />
            <h4 v-if="!tableLoader && fullEventLength === 0 && reportData.length === 0" class="data-empty">
                {{ $t('common.noData') }}
            </h4>
        </div>
    </div>
</template>

<script type="text/babel">
import { mapState, mapActions } from 'vuex';
import CallTraceModel from './CallTraceModel';
import Pagination from '../../common/Pagination.vue';
import Chartist from 'ascom-chartist-logscale';
import Hide from '../../common/Hide.vue';
import {
    resourceFullEventReport,
    resourceFullEventCallStatistics,
    resourceFullEventLength,
} from '../../../api/resources';
import { getCancelToken } from '../../../api';
import { doScroll } from '../../common/helpers';
import {
    fullEventChartConfiguration,
    showColoredIcon,
    iconClass,
    flipThePage,
    toCachePages,
} from '../helpers/reportsHelper';
import LoadingBlock from '../../common/LoadingBlock';
import some from 'lodash-es/some';
import isEqual from 'lodash-es/isEqual';
import { ERROR } from '../../../constants';

export default {
    components: {
        Pagination,
        Hide,
        LoadingBlock,
    },

    props: {
        reportParameters: {
            type: Object,
            required: true,
        },
    },

    data() {
        return {
            reportData: [],
            callStatistics: undefined,
            fullEventLength: 0,
            cache: {},
            pagination: {
                current_page: 1,
                last_page: 0,
                per_page: 5,
            },
            tableLoader: true,
            chartLoader: true,
            chartVisibility: true,
            tableVisibility: true,
            cancelToken: getCancelToken(),
            cancelPaginationToken: getCancelToken(),
        };
    },

    computed: {
        ...mapState({ listAA: 'assignmentArea', listCallTypes: 'callTypes' }),

        /**
         * Call statistics chart loading status
         * @returns {Boolean}
         */
        callStatisticsLoaded() {
            return !this.chartLoader && !!this.callStatistics && this.emptyCallStatistics;
        },

        /**
         * Returns if callStatistics object is empty
         * @returns {Boolean}
         */
        emptyCallStatistics() {
            return some(this.callStatistics, el => !!el);
        },
    },

    watch: {
        reportParameters(newParameters, oldParameters) {
            if (!isEqual(newParameters, oldParameters)) {
                this.resetToDefault();
                this.loadReport();
            }
        },

        'pagination.current_page'() {
            doScroll(this.$refs.details);
        },

        chartVisibility(value) {
            if (value) {
                if (this.callStatistics) {
                    this.chartBuilder(this.callStatistics);
                }
            }
        },
    },

    created() {
        this.flagId = null;
    },

    mounted() {
        this.loadReport();
    },

    beforeDestroy() {
        this.cancelToken.cancel();
        this.cancelPaginationToken.cancel();
    },

    methods: {
        showColoredIcon,
        iconClass,
        toCachePages,

        ...mapActions({
            popup: 'popup/popup',
        }),

        /**
         * Load report data. In case of error will be displayed popup message with text.
         */
        async loadReport() {
            try {
                await Promise.all([this.loadCallStatistics(), this.loadFullEventLengthAndTableData()]);
            } catch (error) {
                this.tableLoader = false;
                this.chartLoader = false;
                if (error !== 'canceled' && error.data && error.data.error) {
                    this.popup({
                        type: ERROR,
                        header: this.$t('common.error'),
                        text: this.$t(error.data.error),
                    });
                }
            }
        },

        /**
         * Getting count of records in the table. If value > 0 then will be initialized loadTableData function.
         */
        async loadFullEventLengthAndTableData() {
            this.fullEventLength = (
                await resourceFullEventLength({
                    data: this.reportParameters,
                    cancelToken: this.cancelToken.token,
                })
            ).data;

            if (this.fullEventLength) {
                this.pagination.last_page = Math.ceil(this.fullEventLength / this.pagination.per_page);
                this.loadTableData();
            } else {
                this.tableLoader = false;
            }
        },

        /**
         * Loading data for Call statistics chart. Initializing chartBuilder function if data is not null
         * for building chart.
         */
        async loadCallStatistics() {
            this.callStatistics = (
                await resourceFullEventCallStatistics({
                    data: this.reportParameters,
                    cancelToken: this.cancelToken.token,
                })
            ).data;

            this.chartLoader = false;
            if (this.callStatistics) {
                this.chartLoader = false;
                this.chartBuilder(this.callStatistics);
            }
        },

        /**
         * Set series and options for chart.
         * @param {object} data
         */
        chartBuilder(data) {
            let chartData = {
                series: [
                    {
                        meta: this.$t('reports.fullEvent.graph.callWaiting'),
                        value: data.call_waiting_time || 0,
                        className: 'ct-call-waiting-bar',
                    },
                    {
                        meta: this.$t('reports.fullEvent.graph.nursePresence'),
                        value: data.nurse_presence_time || 0,
                        className: 'ct-nurse-presence-bar',
                    },
                    {
                        meta: this.$t('reports.fullEvent.graph.other'),
                        value: data.other_time || 0,
                        className: 'ct-speech-connect-bar',
                    },
                ],
            };
            let options = fullEventChartConfiguration(chartData.series);

            new Chartist.Pie('.full-event', chartData, options);
        },

        handlePaginationCallback() {
            this.tableLoader = false;
            flipThePage.call(this);
        },

        /**
         * Loading table data depends on pagination parameters. By default, the data is loaded from the 0 page.
         * @return {Promise<void>}
         */
        loadTableData() {
            this.tableLoader = true;
            let data = {
                ...this.reportParameters,
                limit: 5,
                offset: (this.pagination.current_page - 1) * this.pagination.per_page,
            };

            return resourceFullEventReport({ data, cancelToken: this.cancelPaginationToken.token })
                .then(({ data }) => {
                    this.reportData = data.map(
                        callTrace => new CallTraceModel(callTrace, this.listAA, this.listCallTypes)
                    );
                    this.cache[this.pagination.current_page] = this.reportData;
                })
                .catch(response => {
                    // TODO Handler error (https://jira.ascom-ws.com/browse/MERA-1546)
                    return response;
                })
                .then(response => {
                    if (response !== 'canceled') {
                        this.tableLoader = false;
                    }
                });
        },

        hasMarker(id) {
            if (this.flagId === id) {
                return false;
            }
            this.flagId = id;

            return true;
        },

        resetToDefault() {
            this.reportData = [];
            this.callStatistics = undefined;
            this.fullEventLength = 0;
            this.cache = {};
            this.pagination = {
                current_page: 1,
                last_page: 0,
                per_page: 5,
            };
            this.tableLoader = true;
            this.chartLoader = true;
        },
    },
};
</script>
